/** Small checks shared by single-image tools: is this file an image at all, and can the browser open it. */

const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp|svg|avif|heic|heif|tiff?|ico)$/i;

/** True when the file claims to be a picture (by type or by extension). Says nothing about whether it opens. */
export function looksLikeImage(f: { name: string; type: string }): boolean {
  return (f.type || "").toLowerCase().startsWith("image/") || IMAGE_EXT.test(f.name);
}

export const notAnImageMessage = (name: string) =>
  `"${name}" is not a picture this tool can open. Choose a JPG, PNG or WebP image.`;

export const unreadableImageMessage = (name: string) =>
  `"${name}" could not be read as an image. The file may be damaged or may not be a picture, even if its name ends in .jpg or .png.`;

/** Opens the file as an image in the browser; resolves to its size, or null when the browser cannot decode it. */
export function probeImage(file: Blob): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img.naturalWidth > 0 && img.naturalHeight > 0 ? { width: img.naturalWidth, height: img.naturalHeight } : null); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/** "123 KB" / "1.2 MB" / "0.4 KB" -- never "0 KB" for a non-empty file. */
export function fmtSize(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(bytes >= 10240 ? 0 : 1)} KB`;
  return `${bytes} B`;
}

/** Saving as a whole percent: 40 means 40% smaller, negative means larger. */
export function savingPercent(before: number, after: number): number {
  if (!before) return 0;
  return Math.round((1 - after / before) * 100);
}
